/**
 * MemorySchedulerCard — Displays dreaming scheduler status
 *
 * Shows:
 * - Scheduler state (running/stopped)
 * - Last run time and duration
 * - Last run statistics
 * - Next scheduled run
 */

import React, { useState } from 'react';
import {
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Play,
  Pause,
  CheckCircle2,
  Calendar,
  Timer,
  Moon
} from 'lucide-react';

function MemorySchedulerCard({ data, isLoading, error, onRefresh }) {
  const [expanded, setExpanded] = useState(true);

  if (isLoading) {
    return (
      <div className="md-card md-card--loading">
        <div className="md-card-header">
          <Clock size={18} />
          <span>Scheduler</span>
        </div>
        <div className="md-card-loading">
          <Loader2 size={24} className="md-spin" />
          <span>Loading scheduler status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="md-card md-card--error">
        <div className="md-card-header">
          <Clock size={18} />
          <span>Scheduler</span>
          <button className="md-card-refresh" onClick={onRefresh}>
            <RefreshCw size={14} />
          </button>
        </div>
        <div className="md-card-error">
          <AlertTriangle size={20} />
          <span>Failed to load scheduler status</span>
          <code>{error.message}</code>
        </div>
      </div>
    );
  }

  const {
    enabled = false,
    cron_running = false,
    running = false,
    cron = '0 3 * * *',
    last_run,
    last_duration_ms,
    last_stats,
    last_error
  } = data || {};

  const isActive = enabled && cron_running;
  const statusClass = running ? 'md-card--running' : isActive ? 'md-card--pass' : 'md-card--warn';

  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const getTimeSince = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = diffMs / (1000 * 60 * 60);
    if (diffHours < 1) return `${Math.round(diffMs / 60000)} min ago`;
    if (diffHours < 24) return `${Math.round(diffHours)} hours ago`;
    return `${Math.round(diffHours / 24)} days ago`;
  };

  const parseCron = (cronSpec) => {
    // Simple parser for common cron patterns
    const parts = cronSpec.split(' ');
    if (parts.length !== 5) return cronSpec;
    const [minute, hour] = parts;
    if (hour === '*') return 'Every hour';
    return `Daily at ${hour.padStart(2, '0')}:${minute.padStart(2, '0')} PT`;
  };

  return (
    <div className={`md-card ${statusClass}`}>
      <div
        className="md-card-header md-card-header--clickable"
        onClick={() => setExpanded(!expanded)}
      >
        <Moon size={18} />
        <span>Dreaming Scheduler</span>
        <div className="md-card-header-right">
          {running ? (
            <span className="md-status-badge md-status-badge--running">
              <Loader2 size={12} className="md-spin" /> RUNNING
            </span>
          ) : isActive ? (
            <span className="md-status-badge md-status-badge--pass">
              <Play size={12} /> ACTIVE
            </span>
          ) : (
            <span className="md-status-badge md-status-badge--warn">
              <Pause size={12} /> INACTIVE
            </span>
          )}
          <button className="md-card-refresh" onClick={(e) => { e.stopPropagation(); onRefresh(); }}>
            <RefreshCw size={14} />
          </button>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="md-card-content">
          {/* Schedule Info */}
          <div className="md-scheduler-info">
            <div className="md-scheduler-row">
              <Calendar size={14} />
              <span className="md-scheduler-label">Schedule:</span>
              <span className="md-scheduler-value">{parseCron(cron)}</span>
            </div>
            <div className="md-scheduler-row">
              <Clock size={14} />
              <span className="md-scheduler-label">Last Run:</span>
              <span className="md-scheduler-value">
                {formatTime(last_run)}
                {last_run && <span className="md-scheduler-ago">({getTimeSince(last_run)})</span>}
              </span>
            </div>
            <div className="md-scheduler-row">
              <Timer size={14} />
              <span className="md-scheduler-label">Duration:</span>
              <span className="md-scheduler-value">{formatDuration(last_duration_ms)}</span>
            </div>
          </div>

          {/* Last Run Stats */}
          {last_stats && (
            <div className="md-scheduler-stats">
              <h5 className="md-scheduler-stats-title">Last Run Results</h5>
              <div className="md-metrics-grid md-metrics-grid--compact">
                <div className="md-metric">
                  <span className="md-metric-label">Users Processed</span>
                  <span className="md-metric-value">{last_stats.usersProcessed ?? '-'}</span>
                </div>
                <div className="md-metric">
                  <span className="md-metric-label">Conversations</span>
                  <span className="md-metric-value">{last_stats.conversationsDreamed ?? '-'}</span>
                </div>
                <div className="md-metric">
                  <span className="md-metric-label">Facts Extracted</span>
                  <span className="md-metric-value">{last_stats.factsExtracted ?? '-'}</span>
                </div>
                <div className="md-metric">
                  <span className="md-metric-label">Facts Consolidated</span>
                  <span className="md-metric-value">{last_stats.factsConsolidated ?? '-'}</span>
                </div>
                <div className="md-metric">
                  <span className="md-metric-label">Reflections</span>
                  <span className="md-metric-value">{last_stats.reflectionsCreated ?? '-'}</span>
                </div>
                <div className="md-metric">
                  <span className="md-metric-label">Decay Updates</span>
                  <span className="md-metric-value">{last_stats.decayUpdates ?? '-'}</span>
                </div>
                <div className="md-metric">
                  <span className="md-metric-label">Health Updates</span>
                  <span className="md-metric-value">{last_stats.healthUpdates ?? '-'}</span>
                </div>
                <div className="md-metric">
                  <span className="md-metric-label">Errors</span>
                  <span className={`md-metric-value ${last_stats.errorCount > 0 ? 'md-metric-value--error' : ''}`}>
                    {last_stats.errorCount ?? 0}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Last Error */}
          {last_error && (
            <div className="md-scheduler-error">
              <AlertTriangle size={14} />
              <span>Last error: {last_error}</span>
            </div>
          )}

          {/* Success Indicator */}
          {!last_error && last_stats && (
            <div className="md-scheduler-success">
              <CheckCircle2 size={14} />
              <span>Last run completed successfully</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MemorySchedulerCard;
